import React, { useEffect, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { FileText, Plus, Trash2, Edit2, Copy, CopyPlus, Ticket, ExternalLink, Send, Download, CheckCircle2, XCircle, Link2, Upload, Loader2, X, ArrowUp, ArrowDown, Eye, EyeOff, Search } from "lucide-react";
import { toast } from "sonner";
import api, { BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import { FormGatewaySettings } from "./FormGatewaySettings";
import { FormAccountsToggle } from "./FormAccountsToggle";
import { FormReferenceSelect } from "./FormReferenceSelect";
import { FormCouponsPanel } from "./FormCouponsPanel";

const FIELD_TYPES = [
  { key: "short_text", label: "Short text" },
  { key: "long_text", label: "Long text (paragraph)" },
  { key: "number", label: "Number" },
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone" },
  { key: "date", label: "Date" },
  { key: "radio", label: "Single choice (radio)" },
  { key: "checkbox", label: "Multiple choice (checkboxes)" },
  { key: "dropdown", label: "Dropdown" },
  { key: "file", label: "File upload (PDF/JPG/PNG)" },
];
const HAS_OPTIONS = new Set(["radio", "checkbox", "dropdown"]);

const publicUrl = (slug) => `${window.location.origin}/f/${slug}`;

export default function FormsTab({ event }) {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("builder"); // "builder" | "submissions"
  const [editing, setEditing] = useState(null); // form object being edited (null = closed)
  const [subsFormId, setSubsFormId] = useState(null);

  const load = async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const r = await api.get(`/events/${event.event_id}/forms`);
      setForms(r.data || []);
    } catch { toast.error("Failed to load forms"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [event.event_id]);

  const createNew = async () => {
    try {
      const r = await api.post(`/events/${event.event_id}/forms`, {
        name: "Untitled form", description: "", price_paise: 0,
        fields: [], success_message: "Thanks! Your submission has been received.",
      });
      setForms((fs) => [r.data, ...fs]);
      setEditing(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const del = async (f) => {
    if (!window.confirm(`Delete form "${f.name}" and all its submissions?`)) return;
    try { await api.delete(`/forms/${f.form_id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const duplicate = async (f) => {
    try {
      const r = await api.post(`/forms/${f.form_id}/duplicate`);
      toast.success(`Created "${r.data.name}" (draft)`);
      load(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Duplicate failed"); }
  };

  const togglePublish = async (f) => {
    if (!f.published && (f.fields || []).length === 0) {
      if (!window.confirm("This form has no fields. Publish anyway?")) return;
    }
    try {
      await api.post(`/forms/${f.form_id}/publish`, { published: !f.published });
      toast.success(!f.published ? "Published — share the link!" : "Unpublished");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const copyLink = async (f) => {
    try { await navigator.clipboard.writeText(publicUrl(f.slug)); toast.success("Public link copied"); }
    catch { toast.error("Copy failed"); }
  };

  return (
    <div className="space-y-4" data-testid="forms-tab">
      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={() => setView("builder")} className={`px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-2 ${view === "builder" ? "brand-gradient text-white" : "bg-white border border-slate-200 text-slate-600"}`} data-testid="forms-tab-builder">
          <FileText className="w-4 h-4" /> Forms
          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${view === "builder" ? "bg-white/25" : "bg-slate-100 text-slate-500"}`}>{forms.length}</span>
        </button>
        <button onClick={() => setView("submissions")} className={`px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-2 ${view === "submissions" ? "brand-gradient text-white" : "bg-white border border-slate-200 text-slate-600"}`} data-testid="forms-tab-subs">
          <Send className="w-4 h-4" /> Submissions
        </button>
        <button onClick={() => setView("coupons")} className={`px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-2 ${view === "coupons" ? "brand-gradient text-white" : "bg-white border border-slate-200 text-slate-600"}`} data-testid="forms-tab-coupons">
          <Ticket className="w-4 h-4" /> Coupons
        </button>
        <div className="flex-1" />
        {view === "builder" && (
          <button onClick={createNew} className="btn-primary" data-testid="forms-new-btn">
            <Plus className="w-4 h-4" /> New form
          </button>
        )}
      </div>

      {view === "builder" && (
        <>
          {loading ? <div className="dashboard-card p-8 text-center text-slate-400">Loading…</div> :
            forms.length === 0 ? <div className="dashboard-card p-10 text-center text-slate-400">
              <FileText className="w-8 h-8 mx-auto mb-2 text-slate-300" />
              No forms yet. Click <b>New form</b> to build a registration form with pricing + Razorpay checkout.
            </div> : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {forms.map((f) => (
                  <div key={f.form_id} className="dashboard-card p-4 flex flex-col" data-testid={`form-card-${f.form_id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className={`text-[9px] uppercase font-bold px-1.5 py-0.5 rounded-full ${f.published ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{f.published ? "Live" : "Draft"}</span>
                          <span className="font-bold text-slate-800 truncate">{f.name}</span>
                        </div>
                        {f.description && <div className="text-[11px] text-slate-500 mt-1 line-clamp-2">{f.description}</div>}
                        <div className="flex flex-wrap gap-1 mt-2 text-[10px]">
                          <span className="px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600">{(f.fields || []).length} field{(f.fields || []).length === 1 ? "" : "s"}</span>
                          {f.price_paise > 0 ? <span className="px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700 font-bold">₹{(f.price_paise / 100).toLocaleString("en-IN")}</span> : <span className="px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-bold">Free</span>}
                          <span className="px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700">✓ {f.submission_counts?.paid || 0} paid</span>
                          <span className="px-1.5 py-0.5 rounded-full bg-rose-50 text-rose-700">× {f.submission_counts?.failed || 0} failed</span>
                          <span className="px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-800" data-testid={`form-ref-${f.form_id}`}>Ref: {f.reference_name || (f.pm_name ? `${f.pm_name} (PM)` : "Project manager")}</span>
                          {f.accounts_enabled === false && <span className="px-1.5 py-0.5 rounded-full bg-slate-200 text-slate-700 font-bold">Accounts off</span>}
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-1.5 flex-wrap">
                      <button onClick={() => setEditing(f)} className="btn-ghost text-xs py-1.5" data-testid={`form-edit-${f.form_id}`}><Edit2 className="w-3 h-3" /> Edit</button>
                      <button onClick={() => togglePublish(f)} className={`btn-ghost text-xs py-1.5 ${f.published ? "text-amber-700" : "text-emerald-700"}`} data-testid={`form-pub-${f.form_id}`}>
                        {f.published ? <><EyeOff className="w-3 h-3" /> Unpublish</> : <><Eye className="w-3 h-3" /> Publish</>}
                      </button>
                      {f.published && <button onClick={() => copyLink(f)} className="btn-ghost text-xs py-1.5" data-testid={`form-copy-${f.form_id}`}><Copy className="w-3 h-3" /> Copy link</button>}
                      {f.published && <a href={publicUrl(f.slug)} target="_blank" rel="noreferrer" className="btn-ghost text-xs py-1.5"><ExternalLink className="w-3 h-3" /> Open</a>}
                      <button onClick={() => { setSubsFormId(f.form_id); setView("submissions"); }} className="btn-ghost text-xs py-1.5" data-testid={`form-subs-${f.form_id}`}><Send className="w-3 h-3" /> Submissions</button>
                      <button onClick={() => duplicate(f)} className="btn-ghost text-xs py-1.5" data-testid={`form-dup-${f.form_id}`}><CopyPlus className="w-3 h-3" /> Duplicate</button>
                      <button onClick={() => del(f)} className="btn-ghost text-xs py-1.5 text-rose-600 ml-auto" data-testid={`form-del-${f.form_id}`}><Trash2 className="w-3 h-3" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
        </>
      )}

      {view === "submissions" && <SubmissionsPanel forms={forms} activeFormId={subsFormId} onFormChange={setSubsFormId} />}
      {view === "coupons" && <FormCouponsPanel eventId={event.event_id} />}

      {editing && <FormBuilderModal form={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

// ============================================================
// FORM BUILDER MODAL
// ============================================================
function FormBuilderModal({ form, onClose, onSaved }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [state, setState] = useState({
    name: form.name || "",
    description: form.description || "",
    header_banner_url: form.header_banner_url || "",
    price_paise: form.price_paise || 0,
    success_message: form.success_message || "Thanks!",
    fields: JSON.parse(JSON.stringify(form.fields || [])),
    allow_bulk: !!form.allow_bulk,
    max_bulk_count: form.max_bulk_count || 30,
    kaimera_share_paise: form.kaimera_share_paise || 0,
    accounts_enabled: form.accounts_enabled !== false,
    reference_user_id: form.reference_user_id || null,
  });
  const [saving, setSaving] = useState(false);
  const [priceRupees, setPriceRupees] = useState(((form.price_paise || 0) / 100).toString());
  const [kaimeraShareRupees, setKaimeraShareRupees] = useState(((form.kaimera_share_paise || 0) / 100).toString());

  const uploadBanner = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setState((s) => ({ ...s, header_banner_url: r.data.url }));
      toast.success("Banner uploaded");
    } catch { toast.error("Upload failed"); }
  };

  const addField = (type) => setState((s) => ({
    ...s,
    fields: [...s.fields, {
      field_id: `f_${Math.random().toString(36).slice(2, 8)}`,
      type, label: "", required: false,
      options: HAS_OPTIONS.has(type) ? ["Option 1", "Option 2"] : undefined,
      max_size_mb: type === "file" ? 5 : undefined,
    }],
  }));

  const updField = (i, patch) => setState((s) => {
    const fields = [...s.fields]; fields[i] = { ...fields[i], ...patch }; return { ...s, fields };
  });
  const move = (i, dir) => setState((s) => {
    const j = i + dir; if (j < 0 || j >= s.fields.length) return s;
    const fields = [...s.fields]; [fields[i], fields[j]] = [fields[j], fields[i]]; return { ...s, fields };
  });
  const del = (i) => setState((s) => ({ ...s, fields: s.fields.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!state.name.trim()) return toast.error("Form name required");
    setSaving(true);
    try {
      const price_paise = Math.round(parseFloat(priceRupees || "0") * 100) || 0;
      // Only admin can push the Kaimera-share override to the backend. For
      // any other role we omit the key entirely so the server never sees
      // it — the API also strips it defensively but omitting here keeps
      // the audit trail clean.
      const payload = { ...state, price_paise };
      let shareChanged = false;
      if (isAdmin) {
        payload.kaimera_share_paise = Math.round(parseFloat(kaimeraShareRupees || "0") * 100) || 0;
        shareChanged = payload.kaimera_share_paise !== (form.kaimera_share_paise || 0);
        if (shareChanged) {
          const rupees = (payload.kaimera_share_paise / 100).toLocaleString("en-IN");
          const ok = window.confirm(
            `Update Kaimera share to ₹${rupees} per delegate?\n\n` +
            `Every past receipt on this form will be re-scored — the Kaimera and School Payable ` +
            `journal legs get rewritten so the ledger reflects the new split retroactively. Payouts ` +
            `already made are NOT touched (school balance will adjust). Continue?`
          );
          if (!ok) { setSaving(false); return; }
        }
      } else {
        delete payload.kaimera_share_paise;
        delete payload.accounts_enabled;
      }
      const r = await api.patch(`/forms/${form.form_id}`, payload);
      if (shareChanged && r.data?._kaimera_rewrite) {
        const st = r.data._kaimera_rewrite;
        toast.success(`Saved · Rewrote ${st.receipts_touched} past receipt${st.receipts_touched === 1 ? "" : "s"} (${st.entries_written} journal legs updated)`);
      } else {
        toast.success("Saved");
      }
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const banner = state.header_banner_url ? (state.header_banner_url.startsWith("http") ? state.header_banner_url : `${BACKEND_URL}${state.header_banner_url}`) : null;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[92vh] flex flex-col shadow-2xl" data-testid="form-builder-modal">
        <div className="px-6 py-4 border-b border-slate-100 flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase font-bold text-orange-600">Form builder</div>
            <h3 className="font-display font-black text-xl">Edit form</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-1.5 rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-900 shrink-0"
            data-testid="form-builder-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <div>
            <div className="text-[10px] font-bold text-slate-500 uppercase mb-1">Header banner (3:1 image works best)</div>
            {banner ? (
              <div className="relative rounded-xl overflow-hidden border border-slate-200">
                <img src={banner} alt="" className="w-full aspect-[3/1] object-cover" />
                <button onClick={() => setState({ ...state, header_banner_url: "" })} className="absolute top-2 right-2 bg-black/60 text-white rounded-full p-1 hover:bg-black/80"><X className="w-4 h-4" /></button>
              </div>
            ) : (
              <label className="btn-ghost cursor-pointer w-full py-6 justify-center border-2 border-dashed border-slate-200 hover:border-orange-300">
                <Upload className="w-4 h-4" /> Upload banner (image)
                <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadBanner(e.target.files?.[0])} data-testid="form-banner-upload" />
              </label>
            )}
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] font-bold text-slate-500 uppercase mb-1">Form name</div>
              <input className="input-pill" value={state.name} onChange={(e) => setState({ ...state, name: e.target.value })} data-testid="form-name-input" />
            </div>
            <div>
              <div className="text-[10px] font-bold text-slate-500 uppercase mb-1">Registration fee (₹, 0 = free)</div>
              <input type="number" min="0" step="1" className="input-pill" value={priceRupees} onChange={(e) => setPriceRupees(e.target.value)} data-testid="form-price-input" />
            </div>
          </div>

          {isAdmin && (
            <div className="rounded-xl bg-emerald-50/60 border border-emerald-200 p-4" data-testid="form-kaimera-share-section">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-emerald-100 border border-emerald-200 flex items-center justify-center shrink-0 mt-0.5">
                  <span className="text-emerald-700 font-black text-sm">₹</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900 text-sm inline-flex items-center gap-2">
                    Kaimera share per delegate
                    <span className="text-[9px] font-bold bg-amber-100 text-amber-800 border border-amber-200 px-1.5 py-0.5 rounded-full uppercase">Admin only</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    Flat ₹ amount retained by Kaimera on every submission of this form. The rest of the payment
                    automatically credits <b>School Payable</b> in the ledger. Leave <b>0</b> to fall back to the event
                    default. For bulk registrations this value is <b>multiplied by the delegate count</b> so per-delegate
                    accounting stays accurate. Only admins can change this.
                    <div className="mt-1.5 text-amber-700"><b>Retroactive:</b> saving a new value rewrites every past receipt&apos;s Kaimera / School Payable journal legs on this form so the ledger reflects the new split immediately.</div>
                  </div>
                  <div className="mt-3 flex items-center gap-2">
                    <span className="text-slate-500 text-sm">₹</span>
                    <input
                      type="number" min="0" step="1"
                      className="input-pill w-40"
                      value={kaimeraShareRupees}
                      onChange={(e) => setKaimeraShareRupees(e.target.value)}
                      placeholder="0 = use event default"
                      data-testid="form-kaimera-share-input"
                    />
                    <span className="text-xs text-slate-500">per delegate</span>
                  </div>
                </div>
              </div>
            </div>
          )}
          <div>
            <div className="text-[10px] font-bold text-slate-500 uppercase mb-1">Description</div>
            <textarea rows={3} className="input-pill resize-none" value={state.description} onChange={(e) => setState({ ...state, description: e.target.value })} data-testid="form-desc-input" />
          </div>
          <div>
            <div className="text-[10px] font-bold text-slate-500 uppercase mb-1">Success message (shown after submission)</div>
            <textarea rows={2} className="input-pill resize-none" value={state.success_message} onChange={(e) => setState({ ...state, success_message: e.target.value })} />
          </div>

          <FormGatewaySettings formId={form.form_id} />

          <FormReferenceSelect
            value={state.reference_user_id}
            pmName={form.pm_name}
            onChange={(v) => setState({ ...state, reference_user_id: v })}
          />

          <FormAccountsToggle
            enabled={state.accounts_enabled}
            canEdit={isAdmin}
            onChange={(v) => setState({ ...state, accounts_enabled: v })}
          />

          <div className="rounded-xl bg-sky-50/60 border border-sky-200 p-4">
            <label className="flex items-start gap-3 cursor-pointer" data-testid="form-bulk-toggle">
              <input
                type="checkbox"
                checked={state.allow_bulk}
                onChange={(e) => setState({ ...state, allow_bulk: e.target.checked })}
                className="mt-0.5 w-4 h-4 accent-sky-600"
              />
              <div>
                <div className="font-semibold text-slate-900 text-sm">Allow bulk registration (schools registering multiple delegates)</div>
                <div className="text-xs text-slate-500 mt-1">
                  When enabled, the public form shows a quantity picker (max <b>{state.max_bulk_count}</b>) and per-delegate fields
                  (name, email, phone, committee, portfolio preference). Total = registration fee × delegate count, billed as one Razorpay order.
                </div>
              </div>
            </label>
            {state.allow_bulk && (
              <div className="mt-3 flex items-center gap-2">
                <div className="text-[10px] font-bold text-slate-500 uppercase">Max delegates per submission</div>
                <input type="number" min="2" max="100" step="1" className="input-pill w-24"
                  value={state.max_bulk_count}
                  onChange={(e) => setState({ ...state, max_bulk_count: Math.min(100, Math.max(2, parseInt(e.target.value || "30", 10))) })}
                  data-testid="form-bulk-max-input" />
              </div>
            )}
          </div>

          <div className="pt-4 border-t border-slate-100">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="font-bold text-slate-800">Fields</div>
                <div className="text-[11px] text-slate-500">Drag order using ↑ / ↓ arrows on each row</div>
              </div>
              <div className="flex items-center gap-2">
                <select className="input-pill text-sm w-52" onChange={(e) => { if (e.target.value) { addField(e.target.value); e.target.value = ""; } }} defaultValue="" data-testid="form-add-field-select">
                  <option value="">+ Add field…</option>
                  {FIELD_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                </select>
              </div>
            </div>
            {state.fields.length === 0 && <div className="text-sm text-slate-400 text-center p-6 border-2 border-dashed border-slate-200 rounded-xl">No fields yet. Add one above.</div>}
            <div className="space-y-2">
              {state.fields.map((fld, i) => (
                <FieldEditor key={fld.field_id} field={fld} index={i} total={state.fields.length} onChange={(p) => updField(i, p)} onMove={(d) => move(i, d)} onDelete={() => del(i)} />
              ))}
            </div>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-end gap-2">
          <button onClick={onClose} className="btn-ghost">Cancel</button>
          <button onClick={save} disabled={saving} className="btn-primary px-5 disabled:opacity-50" data-testid="form-save-btn">
            {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : "Save form"}
          </button>
        </div>
      </div>
    </div>
  );
}

function FieldEditor({ field, index, total, onChange, onMove, onDelete }) {
  const setOpt = (idx, v) => {
    const opts = [...(field.options || [])]; opts[idx] = v; onChange({ options: opts });
  };
  return (
    <div className="p-3 rounded-xl border border-slate-200 bg-slate-50/40" data-testid={`form-field-${index}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-[10px] uppercase font-bold text-slate-500">{FIELD_TYPES.find((t) => t.key === field.type)?.label || field.type}</span>
        <div className="flex-1" />
        <button onClick={() => onMove(-1)} disabled={index === 0} className="p-1 text-slate-500 hover:text-orange-600 disabled:opacity-30" title="Move up"><ArrowUp className="w-3.5 h-3.5" /></button>
        <button onClick={() => onMove(1)} disabled={index === total - 1} className="p-1 text-slate-500 hover:text-orange-600 disabled:opacity-30" title="Move down"><ArrowDown className="w-3.5 h-3.5" /></button>
        <button onClick={onDelete} className="p-1 text-rose-500 hover:text-rose-700" title="Delete field"><Trash2 className="w-3.5 h-3.5" /></button>
      </div>
      <div className="grid sm:grid-cols-2 gap-2">
        <input placeholder="Label (shown to user)" className="input-pill py-2 text-sm" value={field.label || ""} onChange={(e) => onChange({ label: e.target.value })} data-testid={`form-field-label-${index}`} />
        <label className="flex items-center gap-2 text-xs text-slate-600 self-center">
          <input type="checkbox" checked={!!field.required} onChange={(e) => onChange({ required: e.target.checked })} /> Required
        </label>
      </div>
      {HAS_OPTIONS.has(field.type) && (
        <div className="mt-2">
          <div className="text-[10px] font-bold text-slate-500 uppercase mb-1">Options</div>
          <div className="space-y-1.5">
            {(field.options || []).map((o, oi) => (
              <div key={oi} className="flex items-center gap-1.5">
                <input value={o} onChange={(e) => setOpt(oi, e.target.value)} className="input-pill py-1.5 text-sm flex-1" />
                <button onClick={() => onChange({ options: (field.options || []).filter((_, x) => x !== oi) })} className="text-rose-500 p-1"><X className="w-3.5 h-3.5" /></button>
              </div>
            ))}
            <button onClick={() => onChange({ options: [...(field.options || []), `Option ${(field.options || []).length + 1}`] })} className="text-xs text-orange-600 hover:underline">+ Add option</button>
          </div>
        </div>
      )}
      {field.type === "file" && (
        <div className="mt-2 flex items-center gap-2">
          <span className="text-xs text-slate-500">Max file size</span>
          <input type="number" min="1" max="50" className="input-pill py-1 text-sm w-20" value={field.max_size_mb || 5} onChange={(e) => onChange({ max_size_mb: parseInt(e.target.value, 10) || 5 })} />
          <span className="text-xs text-slate-500">MB (PDF / JPG / PNG only)</span>
        </div>
      )}
    </div>
  );
}

// ============================================================
// SUBMISSIONS PANEL
// ============================================================
function SubmissionsPanel({ forms, activeFormId, onFormChange }) {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("paid"); // paid | failed
  const [q, setQ] = useState("");

  useEffect(() => {
    if (!activeFormId && forms.length > 0) onFormChange(forms[0].form_id);
    // eslint-disable-next-line
  }, [forms.length]);

  const form = forms.find((f) => f.form_id === activeFormId);

  const load = async () => {
    if (!activeFormId) { setSubs([]); return; }
    setLoading(true);
    try {
      const r = await api.get(`/forms/${activeFormId}/submissions`, { params: { status } });
      setSubs(r.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeFormId, status]);

  const toggleAllotted = async (s) => {
    try {
      await api.patch(`/form-submissions/${s.submission_id}/allotted`, { allotted: !s.allotted });
      setSubs((rows) => rows.map((r) => r.submission_id === s.submission_id ? { ...r, allotted: !s.allotted } : r));
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async (s) => {
    if (!window.confirm("Delete this submission?")) return;
    try { await api.delete(`/form-submissions/${s.submission_id}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const exportXlsx = () => {
    if (!activeFormId) return;
    const token = localStorage.getItem("munners_token");
    window.open(`${BACKEND_URL}/api/forms/${activeFormId}/submissions.xlsx?status=${status}&t=${token}`, "_blank");
  };

  const filtered = useMemo(() => {
    const s = q.toLowerCase();
    if (!s) return subs;
    return subs.filter((x) => {
      const sub = x.submitter || {};
      const hay = `${sub.name || ""} ${sub.email || ""} ${sub.phone || ""} ${x.payment_id || ""} ${x.order_id || ""}`.toLowerCase();
      return hay.includes(s);
    });
  }, [subs, q]);

  return (
    <div className="space-y-3" data-testid="submissions-panel">
      <div className="dashboard-card p-3 flex flex-wrap items-center gap-2">
        <select className="input-pill text-sm w-64" value={activeFormId || ""} onChange={(e) => onFormChange(e.target.value)} data-testid="subs-form-select">
          <option value="">— Pick a form —</option>
          {forms.map((f) => <option key={f.form_id} value={f.form_id}>{f.name} {f.published ? "· LIVE" : "· draft"}</option>)}
        </select>
        <button onClick={() => setStatus("paid")} className={`px-3 py-1.5 rounded-full text-xs font-bold ${status === "paid" ? "bg-emerald-500 text-white" : "bg-emerald-50 text-emerald-700 border border-emerald-200"}`} data-testid="subs-status-paid">
          <CheckCircle2 className="w-3 h-3 inline mr-1" /> Confirmed (paid)
        </button>
        <button onClick={() => setStatus("failed")} className={`px-3 py-1.5 rounded-full text-xs font-bold ${status === "failed" ? "bg-rose-500 text-white" : "bg-rose-50 text-rose-700 border border-rose-200"}`} data-testid="subs-status-failed">
          <XCircle className="w-3 h-3 inline mr-1" /> Failed / Declined
        </button>
        <div className="relative flex-1 min-w-[180px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input className="input-pill text-sm pl-9" placeholder="Search name / email / payment ID" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <button onClick={exportXlsx} disabled={!activeFormId} className="btn-ghost text-xs disabled:opacity-40" data-testid="subs-export"><Download className="w-3 h-3" /> Export Excel</button>
      </div>

      {!activeFormId ? <div className="dashboard-card p-10 text-center text-slate-400">Pick a form to view its submissions.</div>
        : loading ? <div className="dashboard-card p-10 text-center text-slate-400">Loading…</div>
        : filtered.length === 0 ? <div className="dashboard-card p-10 text-center text-slate-400">No submissions in this bucket.</div>
        : (
          <div className="dashboard-card overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase font-bold text-slate-500">
                <tr>
                  <th className="p-3 text-left">Submitter</th>
                  <th className="p-3 text-left">Email / Phone</th>
                  <th className="p-3 text-left">Answers</th>
                  <th className="p-3 text-left">Amount</th>
                  <th className="p-3 text-left">Payment</th>
                  <th className="p-3 text-left">Allotted?</th>
                  <th className="p-3 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s) => (
                  <SubmissionRow key={s.submission_id} sub={s} form={form} onToggleAllotted={() => toggleAllotted(s)} onDelete={() => remove(s)} />
                ))}
              </tbody>
            </table>
          </div>
        )}
    </div>
  );
}

function SubmissionRow({ sub, form, onToggleAllotted, onDelete }) {
  const [open, setOpen] = useState(false);
  const [delegatesOpen, setDelegatesOpen] = useState(false);
  const submitter = sub.submitter || {};
  const answers = sub.answers || {};
  // Bulk registrations don't carry per-answer data (each delegate has its
  // own name/email/phone/committee/portfolio inside `sub.delegates[]`).
  // We render them via a dedicated modal so a single Razorpay order still
  // shows as one row but the accountant / PM can drill into every paying
  // delegate without loading a separate page.
  const isBulk = !!sub.is_bulk && Array.isArray(sub.delegates) && sub.delegates.length > 0;
  return (
    <tr className={sub.allotted ? "bg-emerald-50/60" : ""} data-testid={`sub-row-${sub.submission_id}`}>
      <td className="p-3 align-top">
        <div className="font-semibold text-slate-800">
          {submitter.name || "—"}
          {isBulk && (
            <span className="ml-2 text-[9px] font-bold bg-sky-100 text-sky-700 border border-sky-200 px-1.5 py-0.5 rounded-full uppercase" title={`Bulk order with ${sub.delegates.length} delegates`}>
              Bulk · {sub.delegates.length}
            </span>
          )}
        </div>
        <div className="text-[10px] text-slate-400">{new Date(sub.created_at).toLocaleString()}</div>
      </td>
      <td className="p-3 align-top text-xs">
        <div>{submitter.email}</div>
        <div className="text-slate-500">{submitter.phone}</div>
      </td>
      <td className="p-3 align-top text-xs max-w-xs">
        {isBulk ? (
          <button
            onClick={() => setDelegatesOpen(true)}
            className="text-sky-700 hover:underline text-xs font-semibold inline-flex items-center gap-1"
            data-testid={`sub-delegates-${sub.submission_id}`}
          >
            View {sub.delegates.length} delegate{sub.delegates.length === 1 ? "" : "s"} →
          </button>
        ) : (
          <button onClick={() => setOpen(!open)} className="text-orange-600 hover:underline text-xs" data-testid={`sub-details-${sub.submission_id}`}>{open ? "Hide" : "View"} answers</button>
        )}
        {open && !isBulk && (
          <div className="mt-2 space-y-1">
            {(form?.fields || []).map((f) => {
              const v = answers[f.field_id];
              const disp = v == null ? "—" : (typeof v === "object" ? (v.url ? <a href={v.url.startsWith("http") ? v.url : `${BACKEND_URL}${v.url}`} target="_blank" rel="noreferrer" className="text-orange-600 underline">{v.name || "attachment"}</a> : JSON.stringify(v)) : Array.isArray(v) ? v.join(", ") : String(v));
              return (
                <div key={f.field_id}>
                  <div className="text-[9px] uppercase font-bold text-slate-500">{f.label}</div>
                  <div className="text-slate-700 whitespace-pre-wrap break-words">{disp}</div>
                </div>
              );
            })}
          </div>
        )}
        {delegatesOpen && isBulk && (
          <DelegatesModal sub={sub} onClose={() => setDelegatesOpen(false)} />
        )}
      </td>
      <td className="p-3 align-top text-xs">
        <div className="font-bold">₹{((sub.amount_paise || 0) / 100).toLocaleString("en-IN")}</div>
        {sub.coupon_code && (
          <div className="text-[10px] text-emerald-700 font-semibold" data-testid={`sub-coupon-${sub.submission_id}`}>
            {sub.coupon_code} · −₹{((sub.discount_paise || 0) / 100).toLocaleString("en-IN")}
          </div>
        )}
        <div className={`text-[10px] font-bold uppercase ${sub.payment_status === "paid" ? "text-emerald-600" : "text-rose-600"}`}>{sub.payment_status}</div>
        {isBulk && (
          <div className="text-[10px] text-slate-500 mt-1">
            ₹{((sub.unit_price_paise || (sub.amount_paise / sub.delegate_count) || 0) / 100).toLocaleString("en-IN")} × {sub.delegate_count}
          </div>
        )}
      </td>
      <td className="p-3 align-top text-[10px] text-slate-500 break-all max-w-[160px]">
        {sub.payment_id ? <div>PID {sub.payment_id.slice(-10)}</div> : <div>—</div>}
        {sub.order_id && <div className="text-slate-400">OID {sub.order_id.slice(-10)}</div>}
      </td>
      <td className="p-3 align-top">
        <label className="inline-flex items-center gap-1.5 cursor-pointer" title="Manual allotment marker (does not push to Allotments tab)">
          <input type="checkbox" checked={!!sub.allotted} onChange={onToggleAllotted} className="w-4 h-4 accent-emerald-500" data-testid={`sub-allot-${sub.submission_id}`} />
          <span className={`text-[10px] font-bold ${sub.allotted ? "text-emerald-700" : "text-slate-400"}`}>{sub.allotted ? "Allotted" : "Pending"}</span>
        </label>
      </td>
      <td className="p-3 align-top">
        <button onClick={onDelete} className="text-rose-500 p-1 hover:bg-rose-50 rounded" title="Delete submission"><Trash2 className="w-3.5 h-3.5" /></button>
      </td>
    </tr>
  );
}


// ============================================================
// BULK DELEGATES MODAL
// A single Razorpay order in bulk mode can carry 2 → 100 delegates. Rather
// than blow up the submissions table into 100 rows (loses payer/order
// context, breaks the accountant's mental model of "one payment = one
// row"), we surface every delegate's name / email / phone / committee /
// portfolio in a scrollable modal.
// ============================================================
function DelegatesModal({ sub, onClose }) {
  const delegates = sub.delegates || [];
  const unitRs = (sub.unit_price_paise || (sub.amount_paise / (sub.delegate_count || 1)) || 0) / 100;
  const totalRs = (sub.amount_paise || 0) / 100;
  // Render via portal so the modal escapes the <td>/<tr>/<table> layout
  // context — otherwise the browser re-computes row height every time the
  // mouse crosses the modal (fixed-position node still lives inside a
  // table cell for hit-testing purposes on some engines) and the whole
  // dialog flickers as we move the cursor.
  return createPortal(
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="bulk-delegates-modal">
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-100 flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase font-bold text-sky-600">Bulk registration</div>
            <h3 className="font-display font-black text-xl">{delegates.length} delegate{delegates.length === 1 ? "" : "s"}</h3>
            <div className="text-xs text-slate-500 mt-1">
              Paid by <b>{(sub.submitter || {}).name || "—"}</b> · {(sub.submitter || {}).email || (sub.submitter || {}).phone}
              <span className="mx-2 text-slate-300">·</span>
              ₹{unitRs.toLocaleString("en-IN")} × {delegates.length} = <b>₹{totalRs.toLocaleString("en-IN")}</b>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full text-slate-500 hover:bg-slate-100" aria-label="Close" data-testid="bulk-delegates-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase font-bold sticky top-0">
              <tr>
                <th className="text-left px-4 py-2 w-8">#</th>
                <th className="text-left px-4 py-2">Name</th>
                <th className="text-left px-4 py-2">Contact</th>
                <th className="text-left px-4 py-2">Committee</th>
                <th className="text-left px-4 py-2">Portfolio pref</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {delegates.map((d, i) => (
                <tr key={i} data-testid={`bulk-delegate-row-${i}`}>
                  <td className="px-4 py-2 text-slate-400 font-mono text-xs">{i + 1}</td>
                  <td className="px-4 py-2 font-semibold text-slate-800">{d.name || "—"}</td>
                  <td className="px-4 py-2 text-xs text-slate-500">
                    <div>{d.email || "—"}</div>
                    <div>{d.phone || ""}</div>
                  </td>
                  <td className="px-4 py-2 text-xs">{d.committee_name || d.committee_id || "—"}</td>
                  <td className="px-4 py-2 text-xs">{d.portfolio_preference || d.portfolio || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>,
    document.body,
  );
}
